import { BaseService } from './baseService';
import type { FeedbackItem, FeedbackStats, FeedbackFilters, FeedbackListResponse } from './types';

class FeedbackService extends BaseService {
    /**
     * Get feedback statistics with optional date range filtering
     */
    static async getStats(dateRange?: { start?: string; end?: string }): Promise<FeedbackStats> {
        const params = new URLSearchParams();
        if (dateRange?.start) params.append('startDate', dateRange.start);
        if (dateRange?.end) params.append('endDate', dateRange.end);

        const response = await this.get(`/feedback/stats?${params}`);
        if (!response.ok) {
            throw new Error('Failed to fetch feedback stats');
        }
        return response.json();
    }

    /**
     * Get feedback list with filtering and pagination
     */
    static async getFeedback(filters: FeedbackFilters): Promise<FeedbackListResponse> {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.orderBy) params.append('orderBy', filters.orderBy);
        if (filters.orderDirection) params.append('orderDirection', filters.orderDirection);

        const response = await this.get(`/feedback?${params}`);
        if (!response.ok) {
            throw new Error('Failed to fetch feedback');
        }
        return response.json();
    }

    /**
     * Get presigned URL for feedback screenshot
     */
    static async getScreenshotUrl(feedbackId: number, screenshotKey: string): Promise<{ screenshotUrl: string }> {
        const response = await this.get(`/feedback/${feedbackId}/screenshot?key=${screenshotKey}`);
        if (!response.ok) {
            throw new Error('Failed to get screenshot URL');
        }
        return response.json();
    }

    /**
     * Submit new feedback (supports both parameter styles for compatibility)
     */
    static async submitFeedback(feedback: {
        type: 'bug' | 'improvement';
        description: string;
        url: string;
        userAgent?: string;
        screenshot?: File;
        timestamp?: string;
    }): Promise<{ success: boolean; id: number }> {
        try {
            // Convert screenshot to base64 if present
            let screenshotData: string | undefined;
            if (feedback.screenshot) {
                screenshotData = await this.fileToBase64(feedback.screenshot);
            }

            const jsonData = {
                type: feedback.type,
                description: feedback.description,
                url: feedback.url,
                userAgent: feedback.userAgent,
                timestamp: feedback.timestamp,
                screenshot: screenshotData
            };

            // Debug logging
            console.log('Submitting feedback with JSON data:', {
                type: feedback.type,
                description: feedback.description,
                url: feedback.url,
                userAgent: feedback.userAgent,
                timestamp: feedback.timestamp,
                hasScreenshot: !!screenshotData
            });

            // Use the BaseService post method which sends JSON
            const response = await this.post('/feedback', jsonData);

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`Failed to submit feedback: ${response.status} ${errorText}`);
            }
            
            const result = await response.json();
            console.log('Success response:', result);
            return result;
        } catch (error) {
            console.error('Full error in submitFeedback:', error);
            throw error;
        }
    }

    /**
     * Helper method to convert File to base64 string
     */
    private static fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                // Remove the data URL prefix (e.g., "data:image/png;base64,")
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    }

    /**
     * Submit feedback with screenshot (wrapper for compatibility with FeedbackPopup)
     */
    static async submitFeedbackWithScreenshot(
        feedback: {
            type: 'bug' | 'improvement';
            description: string;
            url: string;
            userAgent?: string;
            timestamp?: string;
        },
        screenshot?: File
    ): Promise<{ success: boolean; id: number }> {
        // Simply call the main submitFeedback method with combined parameters
        return this.submitFeedback({
            ...feedback,
            screenshot
        });
    }

    /**
     * Export feedback data as CSV
     */
    static async exportFeedback(filters: FeedbackFilters): Promise<Blob> {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        params.append('format', 'csv');

        const response = await this.get(`/feedback/export?${params}`);
        if (!response.ok) {
            throw new Error('Failed to export feedback');
        }
        return response.blob();
    }
}

export const feedbackService = FeedbackService;
export { FeedbackService };
